import { config } from '@/config';

/**
 * Runtime gate for Sentry Logs streaming.
 *
 * The Sentry SDK is initialized once (in `app/_layout.tsx`) with `enableLogs: true` so the
 * Logs transport/flusher stay registered. A `beforeSendLog` closure reads
 * `isLogStreamingEnabled()` fresh on every log and drops it when this returns false — this
 * uniformly covers both `console.*` forwards and direct `Sentry.logger.*` calls.
 *
 * The per-stage baseline is `config.enableLogsStreamByDefault`:
 * - stages with it `true` (dev/master/staging): always stream.
 * - stages with it `false` (production): off until an authenticated user opts in via the
 *   STREAM_TV_LOGS property (resolved as `UserModel.shouldStreamTvLogs`).
 */
const streamByDefault = config.enableLogsStreamByDefault;

// Baseline is also the pre-auth / logged-out value.
let streamingEnabled = streamByDefault;

export const isLogStreamingEnabled = (): boolean => streamingEnabled;

/**
 * Set the gate from the authenticated user's resolved `shouldStreamTvLogs` (which already
 * combines the per-stage default with the user's STREAM_TV_LOGS opt-in). Call this once a
 * user is authenticated and whenever that value changes.
 */
export const setLogStreamingEnabled = (enabled: boolean): void => {
  streamingEnabled = enabled;
};

/**
 * Reset the gate to the per-stage default. Call this on logout / no user.
 */
export const resetLogStreaming = (): void => {
  streamingEnabled = streamByDefault;
};
